/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Fragment } from 'react';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet-async';

import { breadcrumbStore, idpApplicationStore, projectStore } from 'stores';
import { Breadcrumb, Dropdown, TopBar } from 'components';
import hasAccess, { actions } from 'utils/user-access';
import { getPageTitle } from 'utils/helpers';
import history from 'utils/history';
import buildSlug from 'utils/buildSlug';

const IdpApplicationHeader = () => {
  const { idpApplication, loading } = idpApplicationStore;
  const { project, folder } = projectStore;
  const pageTitle = loading ? 'Loading...' : idpApplication?.name || 'Idp application not Found';

  const handleDelete = async () => {
    breadcrumbStore.toggleDropdownVisibility();

    if (await idpApplicationStore.delete(idpApplication.id)) {
      if (folder.id) {
        history.push(`/folders/${buildSlug(folder.id, folder.name)}`);
      } else {
        history.push(`/projects/${buildSlug(project.id, project.name)}`);
      }
    }
  };

  const title = idpApplication?.name || 'Loading...';
  return (
    <Fragment>
      <Helmet>
        <title>{getPageTitle(pageTitle)}</title>
      </Helmet>

      <TopBar.Breadcrumbs>
        <Breadcrumb title="Home" variant="link" to="/" data-test="breadcrumb-home" />
        <Breadcrumb
          title={project.name || 'Loading...'}
          variant="link"
          to={`/projects/${buildSlug(project.id, project.name)}`}
          data-test="breadcrumb-project"
        />
        {Boolean(folder.parent) && (
          <Fragment>
            {folder.parent.parentId && <Breadcrumb title="..." variant="text" data-test="grand-parent-dots" />}
            <Breadcrumb
              title={folder.parent.name}
              variant="link"
              to={`/folders/${buildSlug(folder.parent.id, folder.parent.name)}`}
              data-test="grand-parent-folder"
            />
          </Fragment>
        )}
        {Boolean(folder.name) && (
          <Breadcrumb
            title={folder.name}
            variant="link"
            to={`/folders/${buildSlug(folder.id, folder.name)}`}
            data-test="parent-folder"
          />
        )}
        {hasAccess(project, actions.MODIFY_PROJECT) ? (
          <Breadcrumb
            title={title}
            variant="dropdown"
            isBold
            forPage="idp-application"
            data-test="breadcrumb-idp-application-menu"
            handleSubmit={async (name) => {
              breadcrumbStore.finishEditing();
              await idpApplicationStore.rename(name);
            }}
          >
            {/* @ts-expect-error TS2739 */}
            <Dropdown.ListItem
              key="edit"
              onClick={() => {
                breadcrumbStore.toggleDropdownVisibility();
                breadcrumbStore.toggleEditingFor('idp-application');
              }}
              data-test="rename-idp-application"
            >
              Edit name
            </Dropdown.ListItem>
            {/* @ts-expect-error TS2739 */}
            <Dropdown.ListItem key="delete" onClick={handleDelete} data-test="delete-idp-application">
              Delete
            </Dropdown.ListItem>
          </Breadcrumb>
        ) : (
          <Breadcrumb title={title} variant="text" isBold data-test="breadcrumb-idp-application" />
        )}
      </TopBar.Breadcrumbs>
    </Fragment>
  );
};

export default observer(IdpApplicationHeader);
